import type { Ref } from 'vue';
import type { RequiredDeep } from 'type-fest';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { onUnmounted, ref } from 'vue';
import { isCancel } from 'axios';

import { fetchGetJobDraft } from '@/helpers/fetchers';

export type DraftItem =
  RequiredDeep<openapi.JobDraftListContentOpenapi>['results'][number];

export interface DraftsListFetchComposable {
  isFetching: Ref<boolean>;
  items: Ref<DraftItem[] | null>;
  total: Ref<number | undefined>;
  isError: Ref<boolean>;
  fetchDrafts: (page: number, pageSize: number) => Promise<void>;
  cancel: () => void;
}

export default function useDraftsListFetch(): DraftsListFetchComposable {
  let controller: AbortController | undefined;
  const isFetching = ref(false);
  const items = ref<DraftItem[] | null>(null);
  const total = ref<number | undefined>(undefined);
  const isError = ref(false);

  async function fetchDrafts(page: number, pageSize: number): Promise<void> {
    if (controller) controller.abort();
    const thisController = new AbortController();
    controller = thisController;
    isFetching.value = true;
    isError.value = false;
    await fetchGetJobDraft(
      { page, page_size: pageSize },
      { signal: thisController.signal },
    )
      .then((data) => {
        items.value = (data.results ?? []) as DraftItem[];
        total.value = data.count;
      })
      .catch((err: unknown) => {
        if (isCancel(err)) return undefined;
        items.value = null;
        total.value = undefined;
        isError.value = true;
        throw err;
      })
      .finally(() => {
        if (controller === thisController) isFetching.value = false;
      });
  }

  function cancel(): void {
    if (controller) controller.abort();
  }

  onUnmounted(cancel);

  return { isFetching, items, total, isError, fetchDrafts, cancel };
}
